import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import Sidebar from "../Sidebar/sidebar";
import { useLanguage } from "../../context/LanguageContext";
import { useTheme } from "../../context/ThemeContext";

export default function MainLayout({ children }) {
  const { isIndonesian, isEnglish, toggleLanguage, t } = useLanguage();
  const { isLight } = useTheme();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMobileSidebarOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    if (!mobileSidebarOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileSidebarOpen]);

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-paper">
      <div className="hidden h-full shrink-0 md:block">
        <Sidebar />
      </div>

      <div
        className={`fixed inset-0 z-40 md:hidden ${
          mobileSidebarOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        <div
          className={`absolute inset-0 bg-slate-950/50 backdrop-blur-[2px] transition-opacity duration-300 ${
            mobileSidebarOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileSidebarOpen(false)}
        />

        <div
          className={`absolute inset-y-0 left-0 transition-transform duration-300 ${
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <Sidebar
            mobile
            onClose={() => setMobileSidebarOpen(false)}
            onNavigate={() => setMobileSidebarOpen(false)}
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Stable fixed-height mobile top bar — never changes height on scroll */}
        <div className="flex h-13 shrink-0 items-center justify-between px-4 py-2.5 border-b border-ledger-border bg-card-bg md:hidden">
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            className="rounded-lg border border-ledger-border bg-surface-inset p-1.5 text-text-sec transition-colors hover:text-text-pri"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2">
            <img
              src={isLight ? "/tacia-icon.png" : "/tacia-icon-dark.png"}
              alt="Tacia"
              className="h-6 w-6 object-contain"
            />
            <span className="text-sm font-semibold tracking-tight text-text-pri">
              TACIA
            </span>
          </div>

          <button
            type="button"
            onClick={toggleLanguage}
            className="flex items-center gap-1 rounded-lg border border-ledger-border bg-surface-inset px-2.5 py-1 text-[11px] font-semibold tracking-wider text-text-sec transition-colors hover:text-text-pri"
            title={t("sidebar.langSwitchTooltip")}
          >
            <span className={isIndonesian ? "text-text-pri font-bold" : "opacity-40"}>ID</span>
            <span className="opacity-30">|</span>
            <span className={isEnglish ? "text-text-pri font-bold" : "opacity-40"}>EN</span>
          </button>
        </div>

        <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col">
          {children}
        </div>
      </div>
    </div>
  );
}
